import { useEffect, useMemo, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";

import axios from "../../api/axios";
import useDebouncedValue from "../hooks/useDebouncedValue";

const defaultGetOptionLabel = (option) => option?.name || "";
const defaultGetOptionValue = (option) => option?._id || "";
const defaultMapResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
};

export default function AsyncEntityAutocomplete({
    label,
    value,
    onChange,
    endpoint,
    params = {},
    queryParam = "search",
    limit = 50,
    placeholder,
    helperText,
    error = false,
    disabled = false,
    required = false,
    size = "small",
    fullWidth = true,
    getOptionLabel = defaultGetOptionLabel,
    getOptionValue = defaultGetOptionValue,
    mapResponse = defaultMapResponse,
    selectedOption = null,
    noOptionsText = "No matches found.",
    minSearchLength = 1,
}) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState("");
    const debouncedInput = useDebouncedValue(inputValue, 350);
    const paramsKey = JSON.stringify(params);

    const selectedValue = useMemo(() => {
        if (selectedOption && getOptionValue(selectedOption) === value) {
            return selectedOption;
        }

        return options.find((option) => getOptionValue(option) === value) || null;
    }, [getOptionValue, options, selectedOption, value]);

    useEffect(() => {
        if (!open || disabled) return;

        if (debouncedInput.trim().length < minSearchLength) {
            setOptions(selectedValue ? [selectedValue] : []);
            setLoading(false);
            setFetchError("");
            return;
        }

        let active = true;

        const fetchOptions = async () => {
            try {
                setLoading(true);
                setFetchError("");

                const res = await axios.get(endpoint, {
                    params: {
                        ...JSON.parse(paramsKey),
                        [queryParam]: debouncedInput || undefined,
                        limit,
                    },
                });

                if (!active) return;

                const nextOptions = mapResponse(res.data);
                setOptions(
                    selectedValue
                        ? [
                            selectedValue,
                            ...nextOptions.filter(
                                (option) => getOptionValue(option) !== getOptionValue(selectedValue)
                            ),
                        ]
                        : nextOptions
                );
            } catch (err) {
                if (active) {
                    setFetchError(err.response?.data?.message || "Failed to load options.");
                    setOptions(selectedValue ? [selectedValue] : []);
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        fetchOptions();

        return () => {
            active = false;
        };
    }, [
        debouncedInput,
        disabled,
        endpoint,
        getOptionValue,
        limit,
        mapResponse,
        minSearchLength,
        open,
        paramsKey,
        queryParam,
        selectedValue,
    ]);

    return (
        <Autocomplete
            fullWidth={fullWidth}
            sx={{
                width: fullWidth ? "100%" : "auto",
                minWidth: 0,
            }}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={selectedValue}
            options={options}
            loading={loading}
            disabled={disabled}
            inputValue={inputValue}
            onInputChange={(event, nextValue, reason) => {
                setInputValue(nextValue);
            }}
            onChange={(event, option) => {
                onChange?.(option ? getOptionValue(option) : "", option || null);
            }}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, selected) =>
                getOptionValue(option) === getOptionValue(selected)
            }
            noOptionsText={
                fetchError ||
                (inputValue.trim().length < minSearchLength
                    ? `Type at least ${minSearchLength} character${minSearchLength > 1 ? "s" : ""} to search.`
                    : noOptionsText)
            }
            filterOptions={(items) => items}
            renderInput={(renderParams) => (
                <TextField
                    {...renderParams}
                    label={label}
                    placeholder={placeholder}
                    helperText={fetchError || helperText}
                    error={Boolean(error || fetchError)}
                    required={required}
                    size={size}
                    fullWidth={fullWidth}
                    InputProps={{
                        ...renderParams.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                                {renderParams.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
